import React, { useState, useEffect } from 'react';
import './OrderAcceptanceCard.css';

const OrderAcceptanceCard = ({ order, onAccept, onReject, onViewDetails }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(300); // 5 minutes in seconds

  // Calculate time elapsed and remaining since order creation
  useEffect(() => {
    const calculateTimeRemaining = () => {
      if (!order.timestamp) return 300;

      const orderTime = order.timestamp.toDate ? order.timestamp.toDate() : new Date(order.timestamp);
      const now = new Date();
      const elapsedSeconds = Math.floor((now - orderTime) / 1000);
      const remaining = Math.max(0, 300 - elapsedSeconds); // 5 minutes = 300 seconds

      return remaining;
    };

    // Initial calculation
    setTimeRemaining(calculateTimeRemaining());

    // Update every second
    const interval = setInterval(() => {
      const remaining = calculateTimeRemaining();
      setTimeRemaining(remaining);

      // Auto-accept when timer reaches 0
      if (remaining === 0 && !isProcessing) {
        handleAccept();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [order.timestamp]);

  // Format time for display
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  // Format countdown timer
  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get time ago
  const getTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / 60000);

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} mins ago`;
    return `${Math.floor(diffInMinutes / 60)} hours ago`;
  };

  const handleAccept = async () => {
    setIsProcessing(true);
    await onAccept(order.id);
    setIsProcessing(false);
  };

  const handleReject = async () => {
    if (window.confirm('Are you sure you want to reject this order?')) {
      setIsProcessing(true);
      await onReject(order.id);
      setIsProcessing(false);
    }
  };

  const totalItems = order.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  // Get timer color based on remaining time
  const getTimerColor = () => {
    if (timeRemaining > 180) return 'green'; // > 3 mins
    if (timeRemaining > 60) return 'orange'; // > 1 min
    return 'red'; // < 1 min
  };

  // Get order type badge
  const getOrderTypeBadge = () => {
    if (!order.orderType) return null;

    const orderTypeConfig = {
      delivery: { icon: '🚚', text: 'Delivery', className: 'delivery' },
      takeaway: { icon: '🏃', text: 'Takeaway', className: 'takeaway' },
      pickup: { icon: '🏃', text: 'Pickup', className: 'pickup' },
      'dine-in': { icon: '🍽️', text: 'Dine-In', className: 'dine-in' },
      dinein: { icon: '🍽️', text: 'Dine-In', className: 'dine-in' }
    };

    const config = orderTypeConfig[order.orderType.toLowerCase()];
    if (!config) return null;

    return (
      <div className={`order-type-badge ${config.className}`}>
        <span className="badge-icon">{config.icon}</span>
        <span className="badge-text">{config.text}</span>
      </div>
    );
  };

  return (
    <div className="order-acceptance-card">
      {/* New Order Badge */}
      <div className="new-order-badge pulsing">
        <span className="badge-icon">🔔</span>
        <span className="badge-text">NEW ORDER!</span>
      </div>

      {/* Timer Section */}
      <div className={`acceptance-timer ${getTimerColor()}`}>
        <div className="timer-icon">⏱️</div>
        <div className="timer-content">
          <span className="timer-label">Auto-accept in</span>
          <span className="timer-value">{formatCountdown(timeRemaining)}</span>
        </div>
      </div>

      {/* Order Header */}
      <div className="order-acceptance-header">
        <div className="order-id-section">
          <h3 className="order-id">{order.customOrderId || `#${order.id.slice(-6)}`}</h3>
          <span className="order-time">{formatTime(order.timestamp)}</span>
          <span className="order-time-ago">{getTimeAgo(order.timestamp)}</span>
        </div>
        {getOrderTypeBadge()}
      </div>

      {/* Customer Info */}
      <div className="order-acceptance-customer">
        <div className="customer-detail">
          <span className="detail-icon">👤</span>
          <div className="detail-content">
            <span className="detail-label">Customer</span>
            <span className="detail-value">{order.customerName}</span>
          </div>
        </div>
        <div className="customer-detail">
          <span className="detail-icon">📞</span>
          <div className="detail-content">
            <span className="detail-label">Phone</span>
            <span className="detail-value">{order.customerPhone}</span>
          </div>
        </div>
      </div>

      {/* Order Items */}
      <div className="order-acceptance-items">
        <div className="items-header">
          <span className="items-count">{totalItems} {totalItems === 1 ? 'Item' : 'Items'}</span>
          <span className="items-total">₹{order.total}</span>
        </div>
        <div className="items-list">
          {order.items && order.items.slice(0, 3).map((item, index) => (
            <div key={index} className="item-row">
              <span className={`item-veg-indicator ${item.isVeg ? 'veg' : 'nonveg'}`}></span>
              <span className="item-name">{item.name}</span>
              <span className="item-quantity">×{item.quantity}</span>
            </div>
          ))}
          {order.items && order.items.length > 3 && (
            <div className="items-more">
              +{order.items.length - 3} more items
            </div>
          )}
        </div>
      </div>

      {/* Delivery Address (if delivery order) */}
      {order.orderType === 'delivery' && order.deliveryAddress && (
        <div className="order-acceptance-address">
          <div className="address-icon">📍</div>
          <div className="address-content">
            <span className="address-label">Delivery Address</span>
            <span className="address-value">{order.deliveryAddress}</span>
            {order.deliveryLocation && order.deliveryLocation.distance && (
              <span className="address-distance">
                {order.deliveryLocation.distance < 1
                  ? `${Math.round(order.deliveryLocation.distance * 1000)}m away`
                  : `${order.deliveryLocation.distance.toFixed(1)}km away`}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Payment Info */}
      <div className="order-acceptance-payment">
        <div className="payment-method">
          <span className="payment-icon">
            {order.paymentMethod === 'cod' ? '💵' : '💳'}
          </span>
          <span className="payment-text">
            {order.paymentMethod === 'cod' ? 'Cash on Delivery' : 'Paid Online'}
          </span>
        </div>
        {order.paymentStatus && (
          <div className={`payment-status ${order.paymentStatus}`}>
            {order.paymentStatus}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="order-acceptance-actions">
        <button
          className="btn-reject"
          onClick={handleReject}
          disabled={isProcessing}
        >
          <span className="btn-icon">✖</span>
          <span className="btn-text">Reject</span>
        </button>
        <button
          className="btn-accept"
          onClick={handleAccept}
          disabled={isProcessing}
        >
          <span className="btn-icon">✓</span>
          <span className="btn-text">{isProcessing ? 'Accepting...' : 'Accept Order'}</span>
        </button>
      </div>

      {/* View Details Link */}
      <button
        className="btn-view-details"
        onClick={() => onViewDetails(order)}
      >
        View Full Details →
      </button>
    </div>
  );
};

export default OrderAcceptanceCard;
